import { Link } from "react-router-dom";
import type { NewsItem, TopicItem } from "@/mocks/news";
import { formatViews, relativeTime } from "@/utils/news";

type Props = {
  topics: TopicItem[];
  hotList: NewsItem[];
};

export default function HomeSidebar({ topics, hotList }: Props) {
  return (
    <aside className="w-full lg:w-[340px] shrink-0 space-y-5">
      <section className="rounded-lg border border-background-200/80 bg-background-50 overflow-hidden">
        <div className="flex items-center justify-between px-4 h-12 border-b border-background-200/80">
          <h2 className="flex items-center gap-2 text-[15px] font-bold text-foreground-950">
            <i className="ri-fire-line text-primary-500" />
            热门话题
          </h2>
          <span className="font-label text-xs tracking-[0.16em] text-foreground-400">TOPICS</span>
        </div>
        <div className="p-3 space-y-2">
          {topics.slice(0, 4).map((topic, index) => (
            <Link
              key={topic.id}
              to={`/topic/${topic.id}`}
              className="group flex items-center gap-3 p-2 rounded-md hover:bg-background-100 transition-colors cursor-pointer"
            >
              <div className="w-16 h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
                <img
                  src={topic.cover}
                  alt={topic.name}
                  title={`${topic.name} - 专题合集`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-bold text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                  {topic.name}
                </h3>
                <p className="mt-1 text-xs text-foreground-500 line-clamp-2-safe leading-5">
                  {topic.subtitle}
                </p>
              </div>
              <span className="font-label text-base text-background-400 group-hover:text-primary-400 transition-colors shrink-0 w-6 text-right">
                {`${index + 1}`.padStart(2, "0")}
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-background-200/80 bg-background-50 overflow-hidden">
        <div className="flex items-center justify-between px-4 h-12 border-b border-background-200/80">
          <h2 className="flex items-center gap-2 text-[15px] font-bold text-foreground-950">
            <i className="ri-bar-chart-line text-secondary-500" />
            热度榜
          </h2>
          <span className="font-label text-xs tracking-[0.16em] text-foreground-400">HOURLY</span>
        </div>
        <div className="px-4 py-1">
          {hotList.map((item, index) => (
            <Link
              key={item.id}
              to={`/detail/${item.id}`}
              className="group flex items-start gap-3 py-3 border-b border-background-200/60 last:border-b-0 cursor-pointer"
            >
              <span
                className={`w-5 shrink-0 font-label text-lg leading-none mt-0.5 ${
                  index < 3 ? "text-primary-500" : "text-foreground-300"
                }`}
              >
                {`${index + 1}`.padStart(2, "0")}
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="text-[13px] font-medium leading-6 text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2-safe">
                  {item.title}
                </h3>
                <div className="mt-1 flex items-center gap-2 text-[11px] text-foreground-400">
                  <span>{relativeTime(item.publishedAt)}</span>
                  <span className="h-2.5 w-px bg-background-300" />
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line" />
                    {formatViews(item.views)}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="rounded-lg overflow-hidden border border-primary-200 bg-primary-50">
        <div className="px-4 py-4">
          <div className="flex items-center gap-2 text-primary-700">
            <i className="ri-megaphone-line text-lg" />
            <span className="font-bold text-sm">你也有料要爆？</span>
          </div>
          <p className="mt-2 text-xs leading-6 text-foreground-600">
            图片、视频、聊天记录均可提交。我们会对线索来源做匿名处理，核实后再决定是否公开。
          </p>
          <Link
            to="/about"
            className="mt-3 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            提交线索
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </aside>
  );
}